import { Link, useParams } from 'react-router-dom';
import { useTickets } from '../hooks/use-tickets.js';
import { useSprint, useSprintAnalyses } from '../hooks/use-sprint.js';
import { JiraTicket } from './jira-analyses.js';
import { TicketCard } from './ticket-plans.js';

// One ticket's page (ADR-039 section 7): what is asked, the analysis and plan, the drafts, assignment and handover, the
// implementation and the session, in that order, with its decision. To do opens it; Reviews lists the tickets.

/** A Jira reference: `jira:ticket/<site>/<KEY>` */
const JIRA_REF = /^jira:ticket\/[^/]+\/([A-Z][A-Z0-9_]*-\d+)$/;

/**
 * A Jira ticket (ADR-039 step 6b): its analysis and decision, then, once implemented, its change and pull request on
 * the same page; the Sprint tab lists them
 */
function JiraTicketPage({ serverId, isAdmin, externalRef, jiraKey, back }: { serverId: string; isAdmin: boolean; externalRef: string; jiraKey: string; back: React.ReactNode }) {
  const { data, error, isLoading, dataUpdatedAt } = useSprintAnalyses(serverId);
  const { data: sprint } = useSprint(serverId);
  const { data: tickets } = useTickets(serverId);
  const a = (data?.analyses ?? []).find((x) => x.key === jiraKey);
  const implementation = (tickets ?? []).find((t) => t.externalRef === externalRef);
  const view = sprint?.configured ? sprint.boards.find((b) => b.board.id === a?.boardId) : undefined;
  const sprints = view && 'sprints' in view ? (view.sprints ?? []).filter((x) => x.state !== 'closed').map((x) => x.name) : [];
  const urgentPriority = view?.board && 'urgentPriority' in view.board ? view.board.urgentPriority : undefined;
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Reading the ticket…</p>;
  if (error) return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Could not load the ticket: {(error as Error).message}. {back}</p>;
  if (!a && !implementation) {
    return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>This ticket is no longer followed: decided more than a week ago, or never analysed. {back}</p>;
  }
  const titles = new Map((tickets ?? []).map((x) => [x.externalRef, x.issueTitle]));
  return (
    <div className="space-y-3">
      {back}
      {a && <JiraTicket serverId={serverId} a={a} isAdmin={isAdmin} sprints={sprints} now={dataUpdatedAt} urgentPriority={urgentPriority} />}
      {implementation && (
        <section aria-label="Implementation" className="space-y-2">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Implementation</h2>
          <TicketCard serverId={serverId} t={implementation} isAdmin={isAdmin} focused={false} onDecided={() => undefined} titles={titles} />
        </section>
      )}
    </div>
  );
}

export function TicketPage({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { ref: raw } = useParams<{ ref: string }>();
  // The browser's router leaves an encoded « / » of the reference as it is (github:org%2Frepo#1)
  const ref = (() => { try { return decodeURIComponent(raw ?? ''); } catch { return raw ?? ''; } })();
  const jiraKey = JIRA_REF.exec(ref)?.[1];
  const { data, error, isLoading } = useTickets(serverId);
  const backTo = (to: string) => (
    <Link to={to} className="text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
      All the tickets
    </Link>
  );
  const back = backTo(`/servers/${serverId}/reviews`);
  if (jiraKey) return <JiraTicketPage serverId={serverId} isAdmin={isAdmin} externalRef={ref} jiraKey={jiraKey} back={backTo(`/servers/${serverId}/sprint`)} />;
  const t = (data ?? []).find((x) => x.externalRef === ref);
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Reading the ticket…</p>;
  if (error) return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Could not load the ticket: {(error as Error).message}. {back}</p>;
  if (!t) {
    return (
      <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>
        This ticket is no longer followed: decided more than 30 days ago, or never assigned to you. {back}
      </p>
    );
  }
  const titles = new Map((data ?? []).map((x) => [x.externalRef, x.issueTitle]));
  return (
    <div className="space-y-3">
      {back}
      <TicketCard serverId={serverId} t={t} isAdmin={isAdmin} focused={false} onDecided={() => undefined} titles={titles} />
    </div>
  );
}
